import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { toastError, toastSuccess } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { convertNumberToUSFormat, downloadBlobFIle } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const Margin = () => {
	const [margins, setMargins] = useState([]);
	const [groupedData, setGroupedData] = useState([]);
	const { id } = useParams();
	const dispatch = useDispatch();
	const [newQtyCellId, setNewQtyCellId] = useState('');
	const [, setIsUpdateTerm] = useState(false);
	const prevTarget = useRef(null);

	useEffect(() => {
		dispatch(openLoader());
		ApiClient.GET(`${BASE_URL}/order/${id}/margin-summary`, '', true)
			.then((response) => {
				let supplierIds = [];
				let rfqIds = [];
				if (response?.data?.projectedMarginDataList) {
					response?.data?.projectedMarginDataList?.forEach((item) => {
						if (item.isSameSupplier === false && !supplierIds.includes(item.supplierId)) {
							supplierIds.push(item.supplierId);
							rfqIds.push(item.rfqId);
						}
					});
				}
				setMargins(response.data);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	}, [dispatch, id]);

	useEffect(() => {
		//group by supplierId. where supplierId and supplierName will be parent row and into parent there will key named rows which will have all the rows of that supplier
		let groupedData = margins?.projectedMarginDataList?.reduce((acc, item) => {
			if (!acc[item.supplierId]) {
				acc[item.supplierId] = {
					supplierName: item.supplierName,
					rows: []
				};
			}
			acc[item.supplierId].rows.push(item);
			return acc;
		}, {});
		setGroupedData(groupedData);
	}, [margins]);

	const handleDownload = () => {
		dispatch(openLoader());
		ApiClient.GET_FILE_DATA(`${BASE_URL}/order/${id}/report/margin-summary`, '', true)
			.then((response) => {
				downloadBlobFIle(response);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	const onChangeAddNewQty = (e, item) => {
		let { value } = e.target;
		//update setMargins(response.data); with new value of extraQuantityPercentage of every item of array with keeping other values same and other rows same
		let updatedMargins = margins?.projectedMarginDataList?.map((i) => {
			if (i.supplierId === item.supplierId) {
				return { ...i, extraQuantityPercentage: value };
			}
			return i;
		});
		setMargins({ ...margins, projectedMarginDataList: updatedMargins });
	};
	const getMergedRfqIdList = (supplierId) => {
		const mergedRfqIdList = margins?.projectedMarginDataList
			?.filter((item) => item.supplierId === supplierId)
			?.reduce((acc, item) => acc.concat(item.rfqIdList), []);
		return mergedRfqIdList;
	};
	const onAddQty = async (item) => {
		// get rfqIdList from margins?.projectedMarginDataList of rfqIdList with item.supplierId and merge all rfqIdList into one array
		const supplierId = item.supplierId;
		const quoteIds = getMergedRfqIdList(supplierId);
		const body = {
			extraQuantityPercentage: item.extraQuantityPercentage,
			quoteIds
		};

		await ApiClient.POST('updateQuotes', body)
			.then(({ data }) => {
				dispatch(openLoader());
				if (data.success) {
					toastSuccess(data.message);
					dispatch(closeLoader());
				} else {
					toastError(data.message);
				}
			})
			.catch(({ response }) => {
				dispatch(closeLoader());
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const resetAll = () => {
		prevTarget.current = null;
		setNewQtyCellId('');
		setIsUpdateTerm(false);
	};
	return (
		<div className="order-margin-table bg-white mt-3">
			<div className="title mb-3">
				<h3 className="semibold-16 mb-0">Order margin summary (Projected)</h3>
			</div>

			<div className="margin-table">
				<table className="table with-border height-sm">
					<thead>
						<tr>
							<th>Factory</th>
							{/* <th>Style({margins?.projectedMarginDataList?.length})</th> */}
							<th>Design({margins?.projectedMarginDataList?.length})</th>
							<th>PO Number</th>
							<th>PO Qty</th>
							<th>PO value</th>
							<th>Qty Update(%)</th>
							<th>Factory Qty</th>
							<th>Factory cost</th>
							<th>Margin</th>
							<th>Margin(%)</th>
						</tr>
					</thead>
					<tbody>
						{groupedData &&
							Object.keys(groupedData).map(
								(key) =>
									groupedData[key]?.rows?.length > 0 &&
									groupedData[key]?.rows?.map((item, index) => (
										<tr key={key}>
											{index === 0 && (
												<td
													className="factory-column"
													rowSpan={groupedData[key]?.rows?.length}
												>
													{item?.supplierName}
												</td>
											)}
											<td>{item?.referenceNumber}</td>
											<td>{item?.poNumber}</td>
											<td>{item?.poQuantity}</td>
											<td>${item?.poValue}</td>

											<td
												className="qty-update-column"
												onClick={() => {
													setNewQtyCellId(`newItem-${item.supplierId}`);
													setIsUpdateTerm(true);
												}}
											>
												{newQtyCellId === `newItem-${item.supplierId}` ? (
													<input
														type="number"
														autoComplete="off"
														id="term"
														value={item.extraQuantityPercentage}
														onChange={(e) => onChangeAddNewQty(e, item)}
														onFocus={(event) =>
															(prevTarget.current = event.target.value)
														}
														onBlur={() => {
															onAddQty(item);
															resetAll();
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter') {
																onAddQty(item, index);
																resetAll();
															}
														}}
													/>
												) : (
													<span className="gray_dark_02">
														{item.extraQuantityPercentage}
													</span>
												)}
											</td>
											<td>{item?.factoryQuantity}</td>
											<td>${item?.factoryCost}</td>
											<td>
												{Math.sign(item?.marginValue) === -1 ? '-$' : '$'}
												{convertNumberToUSFormat(Math.abs(item?.marginValue))}
											</td>

											<td>
												{isNaN(item?.marginPercentage.toFixed(2))
													? 0
													: item?.marginPercentage?.toFixed(2)}
												%
											</td>
										</tr>
									))
							)}

						<tr>
							<td></td>
							<td></td>
							<td>
								<p className="gray_dark_01 weight-600">Total</p>
							</td>
							<td>{margins?.totalPoQuantity}</td>
							<td>${margins?.totalPoValue}</td>
							<td></td>
							<td>{margins?.totalFactoryQuantity}</td>
							<td>${margins?.totalFactoryCost}</td>
							<td>${margins?.totalMarginValue}</td>
							<td>{margins?.averageMarginPercentage?.toFixed(2)}%</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="table-footer-button">
				<div className="mt-3 text-right">
					<button className="button gray-outline size32 ml-2" onClick={handleDownload}>
						<img src={`${process.env.PUBLIC_URL}/icons/download-dark-20.svg`} alt="" /> Download
					</button>
				</div>
			</div>
		</div>
	);
};

export default Margin;
